import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthProvider";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, collection, getDocs, addDoc } from "firebase/firestore";
import {
  Card, CardContent, Typography, TextField, Button, Alert, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Box
} from '@mui/material';

const API_URL = "https://scrapper-22vm.onrender.com/scrape";

function downloadCSV(data) {
  if (!data.length) return;
  const replacer = (key, value) => value === null ? '' : value;
  const header = Object.keys(data[0]);
  const csv = [
    header.join(','),
    ...data.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
  ].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'leads.csv';
  a.click();
  window.URL.revokeObjectURL(url);
}

const Dashboard = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [keyword, setKeyword] = useState("");
  const [location, setLocation] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [subStatus, setSubStatus] = useState("loading");
  const [plan, setPlan] = useState("");
  const [expiry, setExpiry] = useState("");
  const [tokens, setTokens] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (!currentUser) return;

    const checkSubscription = async () => {
      try {
        const subRef = doc(db, "users", currentUser.uid, "subscriptions", "active");
        const subSnap = await getDoc(subRef);
        if (subSnap.exists()) {
          const data = subSnap.data();
          const now = new Date();
          const expiryDate = data.expiry_date ? new Date(data.expiry_date) : null;
          if (data.status === "active" && expiryDate && expiryDate > now) {
            setSubStatus("active");
            setPlan(data.plan || "");
            setExpiry(data.expiry_date || "");
            setTokens(data.tokens ?? null);
          } else {
            setSubStatus("inactive");
          }
        } else {
          setSubStatus("inactive");
        }

        const subsCol = collection(db, "users", currentUser.uid, "subscriptions");
        const subsSnap = await getDocs(subsCol);
        setHistory(subsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (err) {
        console.error("Subscription check failed:", err);
        setSubStatus("inactive");
      }
    };

    checkSubscription();
  }, [currentUser]);

  useEffect(() => {
    if (subStatus === "inactive") {
      navigate("/payment");
    }
  }, [subStatus, navigate]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate("/login");
  };

  const handleScrape = async (e) => {
    e.preventDefault();
    if (tokens !== null && tokens <= 0) {
      setError("No tokens left. Please renew your subscription.");
      return;
    }

    setLoading(true);
    setError("");
    setResults([]);

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keyword, location })
      });

      if (!response.ok) throw new Error("Scrape failed");

      const data = await response.json();
      setResults(data);

      if (tokens !== null) {
        const subRef = doc(db, "users", currentUser.uid, "subscriptions", "active");
        await setDoc(subRef, { tokens: tokens - 1 }, { merge: true });
        setTokens(tokens - 1);

        const subsCol = collection(db, "users", currentUser.uid, "subscriptions");
        const historyData = {
          ...data,
          plan,
          status: "active",
          tokens: tokens - 1,
          saved_at: new Date().toISOString()
        };
        await addDoc(subsCol, historyData);
      }

      // Save scrape history (keyword, location, results) silently
      if (currentUser) {
        const scrapeHistoryCol = collection(db, "users", currentUser.uid, "scrape_history");
        await addDoc(scrapeHistoryCol, {
          keyword,
          location,
          results: data,
          scraped_at: new Date().toISOString()
        });
      }
    } catch (err) {
      console.error("Scrape failed:", err);
      setError("Failed to fetch leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    navigate("/login");
    return null;
  }

  if (subStatus === "loading") {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Typography>Checking subscription...</Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" mt={4} px={1}>
      <Typography variant="h4" gutterBottom textAlign="center">
        Welcome, {currentUser.email}
      </Typography>

      {plan && expiry && (
        <Typography variant="body1" sx={{ mb: 2 }}>
          Subscription: <b>{plan}</b> (expires on <b>{expiry}</b>)<br />
          {tokens !== null && <span>Tokens left: <b>{tokens}</b></span>}
        </Typography>
      )}

      <Button onClick={handleLogout} variant="outlined" sx={{ mb: 3, width: '100%', maxWidth: 370 }}>
        Logout
      </Button>

      <Card sx={{ width: '100%', maxWidth: 400, mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Scrape Leads</Typography>
          <form onSubmit={handleScrape} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <TextField
              label="Keyword (e.g. clothing company)"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Location (optional)"
              value={location}
              onChange={e => setLocation(e.target.value)}
              fullWidth
            />
            <Button type="submit" variant="contained" disabled={loading || (tokens !== null && tokens <= 0)} fullWidth>
              Scrape
            </Button>
          </form>
          {loading && <Typography sx={{ mt: 2 }}>Loading...</Typography>}
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </CardContent>
      </Card>

      {results.length > 0 && (
        <Box width="100%" maxWidth={900} overflow="auto">
          <Button
            variant="contained"
            color="success"
            sx={{ mt: 2, mb: 2, width: '100%', maxWidth: 300 }}
            onClick={() => downloadCSV(results)}
          >
            Download CSV
          </Button>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Company Name</TableCell>
                  <TableCell>Email(s)</TableCell>
                  <TableCell>Phone(s)</TableCell>
                  <TableCell>Website</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((lead, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{lead.company_name}</TableCell>
                    <TableCell>{lead.emails}</TableCell>
                    <TableCell>{lead.phones}</TableCell>
                    <TableCell>
                      <a href={lead.website} target="_blank" rel="noopener noreferrer">{lead.website}</a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {history.length > 0 && (
        <Box width="100%" maxWidth={900} mt={4}>
          <Typography variant="h6" gutterBottom>Subscription History</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Last Payment</TableCell>
                  <TableCell>Tokens</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>{h.id}</TableCell>
                    <TableCell>{h.status}</TableCell>
                    <TableCell>{h.plan}</TableCell>
                    <TableCell>{h.start_date}</TableCell>
                    <TableCell>{h.expiry_date}</TableCell>
                    <TableCell>{h.last_payment}</TableCell>
                    <TableCell>{h.tokens}</TableCell>
                    <TableCell>{h.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;